import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImageUp, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useContent } from "../../context/ContentContext";

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
}

// Image with an owner-only "Change photo" badge. `k` stores the URL as a content key; or pass `onSave(url)` for entity images.
export function EditableImage({ k, fallback, onSave, value: forced, alt = "", className = "", wrapperClassName = "", label, testId, ...rest }) {
  const { get, save, editMode } = useContent();
  const src = forced !== undefined ? forced : get(k, fallback);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      if (onSave) await onSave(url); else await save(k, url, { type: "image", label: label || k });
      toast.success("Photo updated — live now");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  if (!editMode) return <img src={src} alt={alt} className={className} data-testid={testId} {...rest} />;
  return (
    <span className={`relative inline-block ${wrapperClassName}`} data-edit-key={k}>
      <img src={src} alt={alt} className={`${className} editable-outline`} data-testid={testId} {...rest} />
      <span role="button" tabIndex={0} data-testid={`edit-image-${(k || label || "image").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!busy) inputRef.current?.click(); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); if (!busy) inputRef.current?.click(); } }}
        aria-disabled={busy} aria-label="Change photo"
        className="edit-badge absolute top-2 left-2 z-30 inline-flex items-center gap-1 rounded-full tracking-normal normal-case bg-[#F0B429] text-black text-[11px] font-extrabold px-2 py-1 shadow-lg ring-2 ring-white hover:bg-[#ffc94d]">
        {busy ? <Loader2 size={11} className="animate-spin" /> : <ImageUp size={11} />} {busy ? "Uploading…" : "Change photo"}
      </span>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} onClick={(e) => e.stopPropagation()} />
    </span>
  );
}
